import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TableRowsSkeleton } from "../skeleton/Skeleton";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";
import {
  selectPayments,
  selectPaymentsStatus,
} from "../../features/payments/payments-slice";
import {
  fetchPayments,
  fetchPaymentStatusHistory,
  voidPayment,
} from "../../features/payments/slice-extensions/payments-slice-extension";
import { fetchContracts } from "../../features/contracts/slice-extensions/contracts-slice-extension";
import { fetchPaymentTypes } from "../../features/payment-types/slice-extensions/payment-types-slice-extension";
import { Payment } from "../../features/payments/models/Payment";
import { PaymentStatus } from "../../features/payments/models/PaymentStatus";
import { formatMoney } from "../../features/billing/utils/money";
import { exportToExcel } from "../../features/billing/utils/excelExport";
import { fetchAllPaymentsForExport } from "../../api/clients/billing-export-client";
import { useMemberNameLookup } from "../../features/members/hooks/useMemberNameLookup";
import { LoadingStatus } from "../../features/sessions/models/LoadingStatus";
import FormModal from "./FormModal";
import PagerControls from "./PagerControls";
import PaymentForm from "./PaymentForm";
import PaymentRow from "./PaymentRow";
import NoteModal from "./NoteModal";
import MemberDirectoryWarning from "./MemberDirectoryWarning";
import {
  selectCollectionAccount,
} from "../../features/collection/collection-slice";
import {
  fetchCollectionAccount,
} from "../../features/collection/slice-extensions/collection-slice-extension";
import { CollectionAccountStatus } from "../../features/collection/models/CollectionAccountStatus";
import { paymentShapeLabel } from "../../features/payments/utils/paymentLabels";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

const PAGE_SIZE = 20;

/** Payments management: list + filter, create pack/drop-in, confirm / receive / overdue / void. */
const BillingPaymentsTab: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const payments = useAppSelector(selectPayments);
  const status = useAppSelector(selectPaymentsStatus);
  const page = useAppSelector((s: RootState) => s.payments.page);
  const totalCount = useAppSelector((s: RootState) => s.payments.totalCount);
  const historyById = useAppSelector((s: RootState) => s.payments.statusHistoryById);
  const memberName = useMemberNameLookup();

  const [searchParams, setSearchParams] = useSearchParams();
  const contractFilter = searchParams.get("contractId") ?? undefined;
  // A payment card in Messages opens here: the member's payments with that payment unfolded.
  const memberFilter = searchParams.get("memberId") ?? undefined;
  const focusPaymentId = searchParams.get("paymentId");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [formMode, setFormMode] = useState<"pack" | "standalone" | null>(null);
  const [voiding, setVoiding] = useState<Payment | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(focusPaymentId);
  const [collectError, setCollectError] = useState<string | null>(null);
  const collectionAccount = useAppSelector(selectCollectionAccount);
  const collectionReady = collectionAccount?.status === CollectionAccountStatus.Active;

  const toggleDetails = (paymentId: string) => {
    const next = expandedId === paymentId ? null : paymentId;
    setExpandedId(next);
    if (next) dispatch(fetchPaymentStatusHistory(next));
  };

  const load = (toPage: number) =>
    dispatch(fetchPayments({
      page: toPage,
      pageSize: PAGE_SIZE,
      status: (statusFilter || undefined) as PaymentStatus | undefined,
      contractId: contractFilter,
      memberId: memberFilter,
    }));

  const clearContractFilter = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("contractId");
    setSearchParams(params);
  };

  const clearMemberFilter = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("memberId");
    params.delete("paymentId");
    setSearchParams(params);
  };

  const filterByContract = (contractId: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", "payments");
    params.set("contractId", contractId);
    setSearchParams(params);
  };

  useEffect(() => {
    load(1);
    // The create forms need contracts + the price catalog ready.
    dispatch(fetchContracts({ page: 1, pageSize: 100 }));
    dispatch(fetchPaymentTypes({ includeArchived: true }));
    // The "Collect" action only shows once the collection account is known to be ready.
    dispatch(fetchCollectionAccount());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, statusFilter, contractFilter, memberFilter]);

  useEffect(() => {
    if (focusPaymentId) dispatch(fetchPaymentStatusHistory(focusPaymentId));
  }, [dispatch, focusPaymentId]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const handleExport = async () => {
    const all = await fetchAllPaymentsForExport();
    exportToExcel(
      "payments",
      all.map((p) => ({
        [t("billing.table.status")]: t(`billing.paymentStatus.${p.status}`, { defaultValue: p.status }),
        [t("billing.table.member")]: memberName(p.memberId),
        [t("billing.table.shape")]: paymentShapeLabel(p.shape, t),
        [t("billing.table.amount")]: formatMoney(p.amountMinorUnits, p.amountCurrency, getDisplayLocale()),
        [t("billing.table.remainingBalance")]: p.remainingBalance ?? "",
        [t("billing.table.dueDate")]: p.dueDate ?? "",
        [t("billing.table.billingPeriod")]: p.billingPeriodStart ? `${p.billingPeriodStart} → ${p.billingPeriodEnd}` : "",
        [t("billing.table.notes")]: p.notes ?? "",
      })),
    );
  };

  return (
    <div className="mt-4 space-y-6">
      <MemberDirectoryWarning />
      {collectError && (
        <div className="rounded-xl bg-red-100 px-3 py-2 text-sm font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300">
          {collectError}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn btn-primary" onClick={() => setFormMode("standalone")}>
          <Plus size={18} strokeWidth={2} />
          <span>{t("billing.actions.newPayment")}</span>
        </button>
        {contractFilter && (
          <button
            type="button"
            onClick={clearContractFilter}
            className="inline-flex items-center gap-1 rounded-full bg-primary-90 px-3 py-1 text-xs font-medium text-primary-30 hover:bg-primary-80 dark:bg-primary-30 dark:text-primary-90 dark:hover:bg-primary-40"
          >
            <span>{t("billing.payments.filteredByContract")}</span>
            <X size={12} />
          </button>
        )}
        {memberFilter && (
          <button
            type="button"
            onClick={clearMemberFilter}
            className="inline-flex items-center gap-1 rounded-full bg-primary-90 px-3 py-1 text-xs font-medium text-primary-30 hover:bg-primary-80 dark:bg-primary-30 dark:text-primary-90 dark:hover:bg-primary-40"
          >
            <span>{t("billing.payments.filteredByMember", { name: memberName(memberFilter) })}</span>
            <X size={12} />
          </button>
        )}
        <button type="button" className="btn btn-secondary ml-auto" onClick={handleExport}>
          <Download size={16} />
          <span>{t("billing.common.exportToExcel")}</span>
        </button>
        <select
          className="input max-w-[200px]"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">{t("billing.payments.allStatuses")}</option>
          {Object.values(PaymentStatus).map((s) => (
            <option key={s} value={s}>{t(`billing.paymentStatus.${s}`, { defaultValue: s })}</option>
          ))}
        </select>
      </div>

      {formMode && (
        <FormModal onClose={() => setFormMode(null)}>
          <PaymentForm
            mode={formMode}
            onSaved={() => { setFormMode(null); load(1); }}
            onClose={() => setFormMode(null)}
          />
        </FormModal>
      )}

      {status === LoadingStatus.Loading && payments.length === 0 ? (
        <TableRowsSkeleton rows={5} />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-grey-90 dark:border-primary-20">
          <table className="w-full text-sm">
            <thead className="bg-grey-95 text-xs uppercase tracking-wide text-grey-30 dark:bg-primary-20 dark:text-grey-70">
              <tr>
                <th className="px-4 py-3 text-left font-medium">{t("billing.table.status")}</th>
                <th className="px-4 py-3 text-left font-medium">{t("billing.table.member")}</th>
                <th className="px-4 py-3 text-left font-medium">{t("billing.table.shape")}</th>
                <th className="px-4 py-3 text-left font-medium">{t("billing.table.amount")}</th>
                <th className="px-4 py-3 text-left font-medium">{t("billing.table.balance")}</th>
                <th className="px-4 py-3 text-left font-medium">{t("billing.table.duePeriod")}</th>
                <th className="px-4 py-3 text-left font-medium">{t("billing.table.created")}</th>
                <th className="px-4 py-3 text-right font-medium"><span className="sr-only">{t("billing.table.actions")}</span></th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-primary-10 text-grey-20 dark:text-white">
              {payments.map((p) => (
                <PaymentRow
                  key={p.id}
                  payment={p}
                  memberName={memberName(p.memberId)}
                  expanded={expandedId === p.id}
                  history={historyById[p.id] ?? []}
                  collectionReady={collectionReady}
                  onToggleDetails={() => toggleDetails(p.id)}
                  onFilterByContract={filterByContract}
                  onVoid={() => setVoiding(p)}
                  onChanged={() => load(page)}
                  onError={setCollectError}
                />
              ))}
              {payments.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-center text-grey-40 dark:text-grey-80" colSpan={8}>
                    {t("billing.payments.empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <PagerControls page={page} totalPages={totalPages} onPageChange={load} />

      {voiding && (
        <NoteModal
          title={t("billing.payments.voidTitle")}
          description={t("billing.payments.voidDescription", {
            amount: formatMoney(voiding.amountMinorUnits, voiding.amountCurrency, getDisplayLocale()),
            member: memberName(voiding.memberId),
          })}
          confirmLabel={t("billing.payments.voidTitle")}
          noteRequired
          onCancel={() => setVoiding(null)}
          onConfirm={async (note) => {
            await dispatch(voidPayment({ id: voiding.id, request: { note } }));
            setVoiding(null);
            load(page);
          }}
        />
      )}
    </div>
  );
};

export default BillingPaymentsTab;
